/**
 * Where the checkout-widget iframe actually loads a stored ticket link from, and which shows open
 * Fast Checkout at all.
 *
 * `ticket_link` fuses ENVIRONMENT (the origin) with IDENTITY (the event id), and only identity is
 * row-scoped — so a stored link carries a CANONICAL widget origin in every environment, production
 * and preview alike. Two consequences run through this module: the guards test the stored link
 * UNCHANGED and never learn that previews exist, and only the iframe `src` (plus the postMessage
 * target derived from it) gets its origin swapped.
 *
 * The HOST vocabulary itself — `isTixologiWidgetLink`, `canonicalWidgetOrigin`, and the
 * `.includes("")` fail-open story they exist to prevent — is DEFINED in
 * `@punchup/utils/tixologiWidgetOrigin` and re-exported here. It lives at that leaf because its
 * consumers (`extractTixologiEventId` → `@punchup/data`, the `@punchup/venues` Buy-button guards)
 * sit below this package in the dependency graph, and this package declares all of them — any of
 * them importing this module is a cycle turbo refuses outright. Re-exported rather than merely
 * referenced so this module stays the one place a reader looks for the widget-host vocabulary,
 * and so `apps/web` call sites keep a single import.
 *
 * What is defined HERE is the layer that may know about previews and about identity:
 *   - `toWidgetOrigin()` / `widgetOriginOf()` — the preview swap and the postMessage target.
 *   - `isTixologiCheckoutShow()` — the compound identity-OR-host question checkout surfaces ask.
 *   - `widgetTicketsUrl()` — the link a manager shares into an event's tickets page (a private
 *     ticket type's `code`, a seats.io channel's `compCode`), always on the canonical origin.
 *
 * Related: `api-base.ts` does the same job for the ticketing/users split. Service topology and the
 * `TIXOLOGI_*` variable contract: packages/tixologi/CLAUDE.md.
 */

import {
  LOCAL_DEV_HOST,
  canonicalWidgetOrigin,
  isHostOrSubdomainOf,
  isTixologiWidgetLink,
  parseUrlish,
} from "@punchup/utils/tixologiWidgetOrigin";

export {
  canonicalWidgetOrigin,
  isTixologiWidgetLink,
} from "@punchup/utils/tixologiWidgetOrigin";

/**
 * The checkout widget's tickets page for an event, with the one query pair a shared link carries:
 * `code` opens a private-link ticket type, `compCode` a seats.io channel's reserved seats. Built on
 * the CANONICAL origin, never the preview swap: a manager hands this link to buyers, and a preview
 * host in it would be dead within a week (see the module header). The value is URL-encoded.
 */
export function widgetTicketsUrl(
  eventId: string,
  query: { code: string } | { compCode: string }
): string {
  return `${canonicalWidgetOrigin() ?? ""}/event/${eventId}/tickets?${new URLSearchParams(query).toString()}`;
}

/**
 * Does this show check out through Tixologi Fast Checkout? Identity OR host: a Tixologi show can
 * carry a `ticket_link` on a legacy Tixologi domain (or none at all) and must still open Fast
 * Checkout — the /paulelia Sept 10 Roostertail bug.
 *
 * Call sites that deliberately decide from the LINK alone (LegacyComedianShowRow, TourDateRow,
 * VenueShowCarouselItem, the ShowsNearby filters) keep calling `isTixologiWidgetLink` directly;
 * this is only for surfaces that open checkout. When the `tixologi_event_id` backfill lands
 * (docs/trackers/tech-debt-backlog.md), this is the ONE place the host half collapses out of.
 */
export function isTixologiCheckoutShow(
  ticketLink: string | null | undefined,
  tixologiEventId: string | null | undefined
): boolean {
  return !!tixologiEventId || isTixologiWidgetLink(ticketLink);
}

/**
 * The host families a preview widget override may point at — the same families the cross-env
 * validator accepts (`.github/scripts/validate-cross-env-inputs.mjs` allowlists Firebase preview
 * channels by site label) plus the local-dev widget.
 *
 * This is the module-side half of that defence: the workflow is the only legitimate SETTER of
 * `NEXT_PUBLIC_TIXOLOGI_WIDGET_PREVIEW_ORIGIN`, but nothing stops the name being created in the
 * Vercel dashboard by hand — in Production, or as a stored Preview value that would bake into
 * every automatic PR preview. The postMessage identity handoff (auth token + email) follows the
 * swapped origin, so a value outside these families must be inert, not honoured.
 */
const PREVIEW_HOST_SUFFIXES = [".web.app", ".firebaseapp.com"];

function isAllowedPreviewHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  if (isHostOrSubdomainOf(host, LOCAL_DEV_HOST)) return true;
  return PREVIEW_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix));
}

/**
 * The link with its protocol+host swapped to the preview widget, when one is configured.
 *
 * Returns the link UNCHANGED when `NEXT_PUBLIC_TIXOLOGI_WIDGET_PREVIEW_ORIGIN` is unset/blank,
 * when that value is not parseable as a host, when its host is outside the preview allowlist
 * above, or when the link itself does not parse. Never throws.
 *
 * ASYMMETRY WORTH KEEPING: the configured preview value is parsed leniently (a bare host is
 * accepted — a human pastes it out of a Firebase channel comment), but the LINK is parsed strictly.
 * Classifying a stored link may tolerate the shapes the database actually holds; inventing a scheme
 * for a URL we are about to load in an iframe is a guess, so a link that does not parse is returned
 * as-is rather than "fixed".
 *
 * Deliberately NOT guarded by `isTixologiWidgetLink()`: the caller has already established that,
 * and a second guard here would silently no-op the preview for any link shape the predicate does
 * not recognise — the harder failure to debug.
 *
 * `NEXT_PUBLIC_*` values are inlined at BUILD time, so this name must be listed in `turbo.json`'s
 * build-env list for EVERY app that reaches this module — `punchup-web` and `punchup-pages` both
 * mount the checkout widget — or a warm Turbo cache serves a build with the previous value baked
 * in (packages/tixologi/CLAUDE.md → "Env vars").
 */
export function toWidgetOrigin(link: string): string {
  const previewUrl = parseUrlish(
    process.env.NEXT_PUBLIC_TIXOLOGI_WIDGET_PREVIEW_ORIGIN
  );
  if (!previewUrl || !isAllowedPreviewHost(previewUrl.hostname)) return link;

  let rewritten: URL;
  try {
    rewritten = new URL(link);
  } catch {
    return link;
  }

  // Assign the three parts separately rather than `.host`: the `host` setter leaves the existing
  // port in place when the new value carries none, so swapping `localhost:3003` for a preview host
  // would produce `https://preview.example:3003`. Setting `port` last (to "" for a preview origin
  // with no explicit port) is what actually drops it.
  rewritten.protocol = previewUrl.protocol;
  rewritten.hostname = previewUrl.hostname;
  rewritten.port = previewUrl.port;
  return rewritten.toString();
}

/**
 * The origin to postMessage a given link's iframe at, or null if the link cannot be parsed.
 *
 * Derived from the LINK (after any preview swap), never from a bare environment variable. That is
 * the bug this replaces: `new URL(process.env.NEXT_PUBLIC_TIXOLOGI_URL)` throws on the configured
 * bare host `tixologi.com`, the caller catches it into `null`, and the buyer-identity + GA4
 * handoffs then no-op silently for every purchase.
 *
 * Null is the honest answer for an unparseable link — postMessage needs a real origin, and "*" is
 * not an option for a payload carrying an auth token and an email address.
 */
export function widgetOriginOf(link: string): string | null {
  try {
    return new URL(toWidgetOrigin(link)).origin;
  } catch {
    return null;
  }
}
